import { useEffect, useState } from "react";

declare global {
  interface Window {
    /** Rolling render-loop average, written for the screenshot/E2E harness. */
    __cimaraFps?: number;
  }
}

/**
 * Rolling FPS estimate from requestAnimationFrame. The value is exposed both
 * in the HUD (`data-testid="fps"`) and on `window.__cimaraFps` so a harness can
 * assert the render budget without reading pixels.
 */
export function useFps(sampleMs = 1000): number {
  const [fps, setFps] = useState(0);

  useEffect(() => {
    let frame = 0;
    let frames = 0;
    let windowStart = performance.now();

    if (typeof window !== "undefined") window.__cimaraFps = 0;

    const tick = (now: number): void => {
      frames += 1;
      const elapsed = now - windowStart;

      if (elapsed >= sampleMs) {
        // A backgrounded tab pauses rAF; discard that window instead of
        // reporting a misleading single-digit average when it wakes up.
        if (elapsed <= sampleMs * 4) {
          const value = (frames * 1000) / elapsed;
          if (Number.isFinite(value) && value > 0) {
            setFps(value);
            window.__cimaraFps = value;
          }
        }
        frames = 0;
        windowStart = now;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [sampleMs]);

  return fps;
}
